import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogIn, UserPlus, AlertCircle, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../i18n/LangContext";
import { api } from "../../lib/api";
import { validate } from "../../lib/validate";
import { Field, inputProps } from "../site/Primitives";
import { startGoogleLogin } from "./AuthCallback";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.2 14.6 2.2 12 2.2 6.6 2.2 2.2 6.6 2.2 12s4.4 9.8 9.8 9.8c5.7 0 9.4-4 9.4-9.6 0-.6-.1-1.1-.2-1.6H12z" /></svg>
);

export const RoleOptions = ({ value, onChange, idPrefix }) => {
  const { t } = useLang();
  return (
    <div className="chips" role="radiogroup" aria-label={t.auth.role}>
      {["client", "expert"].map((r) => (
        <button key={r} type="button" role="radio" aria-checked={value === r} className="chip" style={{ "--c": r === "client" ? "var(--cyan)" : "var(--amber)" }}
          aria-pressed={value === r} onClick={() => onChange(r)} data-testid={`${idPrefix}-role-${r}`}>
          {value === r ? <Check size={14} aria-hidden="true" /> : <i aria-hidden="true" />}{t.auth.roles[r]}
        </button>
      ))}
    </div>
  );
};

export const AuthPanel = ({ defaultRole = "client", compact }) => {
  const { t } = useLang();
  const a = t.auth;
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login");
  const [values, setValues] = useState({ email: "", password: "", name: "", role: defaultRole });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, error: null });

  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    const rules = { email: ["required", "email"], password: mode === "register" ? ["required", ["min", 8]] : ["required"] };
    if (mode === "register") rules.name = ["required", ["min", 2]];
    const errs = validate(values, rules);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState({ busy: true, error: null });
    try {
      const payload = mode === "login" ? { email: values.email, password: values.password } : values;
      const { data } = await api.post(`/auth/${mode}`, payload);
      signIn(data);
    } catch (err) {
      const s = err?.response?.status;
      setState({ busy: false, error: s === 409 ? a.exists : s === 429 ? a.locked : mode === "login" ? a.invalid : t.errors.generic });
    }
  };

  return (
    <div className={`panel pro-panel auth-panel${compact ? " compact" : ""}`} data-testid="auth-panel">
      <div className="tabs" role="tablist" style={{ marginBottom: 18 }}>
        {["login", "register"].map((m) => (
          <button key={m} type="button" role="tab" className="tab-btn" aria-selected={mode === m} onClick={() => { setMode(m); setState({ busy: false, error: null }); }} data-testid={`auth-mode-${m}`}>
            {m === "login" ? <LogIn aria-hidden="true" /> : <UserPlus aria-hidden="true" />}{m === "login" ? a.loginTab : a.registerTab}
          </button>
        ))}
      </div>
      <form onSubmit={submit} noValidate className="auth-form" data-testid="auth-form">
        {mode === "register" && (
          <div className="f-field"><span className="f-label">{a.role}</span><RoleOptions value={values.role} onChange={(r) => setValues((v) => ({ ...v, role: r }))} idPrefix="auth" /></div>
        )}
        {mode === "register" && (
          <Field id="auth-name" label={a.name} error={errors.name} required><input type="text" autoComplete="name" {...inputProps("auth-name", errors.name, true)} value={values.name} onChange={set("name")} /></Field>
        )}
        <Field id="auth-email" label={a.email} error={errors.email} required><input type="email" autoComplete="email" {...inputProps("auth-email", errors.email, true)} value={values.email} onChange={set("email")} /></Field>
        <Field id="auth-password" label={mode === "register" ? `${a.password} — ${a.passwordHint}` : a.password} error={errors.password} required>
          <input type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} {...inputProps("auth-password", errors.password, true)} value={values.password} onChange={set("password")} />
        </Field>
        {mode === "login" && <Link to="/reset-password" className="link-btn forgot-link" data-testid="auth-forgot-link">{t.reset.forgot}</Link>}
        {state.error && <p className="error-banner" role="alert" style={{ marginTop: 0 }} data-testid="auth-error"><AlertCircle aria-hidden="true" />{state.error}</p>}
        <button type="submit" className="btn btn-primary" disabled={state.busy} data-testid="auth-submit-btn">
          {state.busy ? <Loader2 className="spin" aria-hidden="true" /> : mode === "login" ? <LogIn aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
          {mode === "login" ? a.login : a.register}
        </button>
        <div className="auth-or"><span>{a.or}</span></div>
        <button type="button" className="btn btn-ghost google-btn" onClick={startGoogleLogin} data-testid="auth-google-btn"><GoogleIcon />{a.google}</button>
      </form>
    </div>
  );
};

export const RolePicker = () => {
  const { t } = useLang();
  const { signIn } = useAuth();
  const [role, setRole] = useState("client");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try { signIn((await api.post("/auth/role", { role })).data); } finally { setBusy(false); }
  };
  return (
    <div className="panel pro-panel" style={{ maxWidth: 560 }} data-testid="role-picker">
      <h3 className="sub-title">{t.auth.pickRoleTitle}</h3>
      <p className="pro-intro" style={{ marginTop: 10 }}>{t.auth.pickRoleDesc}</p>
      <RoleOptions value={role} onChange={setRole} idPrefix="role-picker" />
      <div className="f-actions"><span /><button type="button" className="btn btn-primary" disabled={busy} onClick={confirm} data-testid="role-picker-confirm-btn">{t.auth.confirmRole}</button></div>
    </div>
  );
};
